"use client";

import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import {
  LayoutDashboard, Calendar, FileText, Receipt, Radio, Users, Settings,
  Bell, ChevronDown, LogOut, ShieldCheck, MessageSquare, BookOpen,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { PORTAL_ROLE_LABEL, type PortalRole } from "@/lib/portal/types";
import { clearPortalSession, readPortalSession, type PortalSession } from "@/lib/portal/session";
import { createClient } from "@/lib/supabase/client";

const NAV = [
  { href: "/portal/dashboard", label: "Overview",     icon: LayoutDashboard, group: "main" },
  { href: "/portal/events",    label: "Events",       icon: Calendar,        group: "main" },
  { href: "/portal/documents", label: "Documents",    icon: FileText,        group: "main" },
  { href: "/portal/messages",  label: "Messages",     icon: MessageSquare,   group: "main" },
  { href: "/portal/live",      label: "Live Ops",     icon: Radio,           group: "ops",  badge: "LIVE" },
  { href: "/portal/invoices",  label: "Invoices",     icon: Receipt,         group: "fin"  },
  { href: "/portal/plans",     label: "Plans Library",icon: BookOpen,        group: "fin"  },
  { href: "/portal/incidents", label: "Incidents",    icon: ShieldCheck,     group: "fin"  },
  { href: "/portal/users",     label: "Team",         icon: Users,           group: "admin"},
  { href: "/portal/settings",  label: "Settings",     icon: Settings,        group: "admin"},
];

const NAV_GROUPS: { id: string; label: string }[] = [
  { id: "main",  label: "Workspace" },
  { id: "ops",   label: "Operations" },
  { id: "fin",   label: "Finance & Compliance" },
  { id: "admin", label: "Administration" },
];

export default function PortalLayout({ children }: { children: React.ReactNode }) {
  const pathname = usePathname() ?? "";
  const router = useRouter();
  const [orgSwitcherOpen, setOrgSwitcherOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [session, setSession] = useState<PortalSession | null>(null);
  const [ready, setReady] = useState(false);

  // Hide chrome on login/register
  if (pathname.startsWith("/portal/login") || pathname.startsWith("/portal/register")) {
    return <div className="min-h-screen bg-slate-950 text-slate-100">{children}</div>;
  }

  useEffect(() => {
    const localSession = readPortalSession();
    if (!localSession) {
      router.replace("/portal/login");
      return;
    }

    setSession(localSession);
    setReady(true);
  }, [router]);

  if (!ready || !session) {
    return <div className="min-h-screen bg-slate-950" />;
  }

  const role = session.role as PortalRole;
  const initials = session.name.split(" ").map((n) => n[0]).slice(0, 2).join("");

  async function handleSignOut() {
    const supabase = createClient();
    await supabase.auth.signOut();
    clearPortalSession();
    router.replace("/portal/login");
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex">
      {/* ── Sidebar ──────────────────────────────────────────────────────── */}
      <aside className="w-72 border-r border-slate-800 bg-slate-900/60 backdrop-blur sticky top-0 h-screen flex flex-col">
        {/* Brand + org switcher */}
        <div className="p-5 border-b border-slate-800">
          <div className="flex items-center gap-2 mb-4">
            <div className="h-8 w-8 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-400 grid place-items-center font-bold text-white text-sm">O</div>
            <div className="leading-tight">
              <div className="text-sm font-bold tracking-tight">OpsCom</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400">Customer Portal</div>
            </div>
          </div>

          <button
            onClick={() => setOrgSwitcherOpen((o) => !o)}
            className="w-full text-left rounded-xl border border-slate-800 bg-slate-950/60 hover:border-slate-700 px-3 py-2.5 flex items-center gap-2 transition-colors"
          >
            <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 grid place-items-center text-xs font-bold text-white shrink-0">
              {session.orgName.split(" ").map((n) => n[0]).slice(0, 2).join("")}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold truncate">{session.orgName}</div>
              <div className="text-[10px] text-slate-400 uppercase tracking-wider">{session.orgStatus}</div>
            </div>
            <ChevronDown className={cn("h-4 w-4 text-slate-500 transition-transform", orgSwitcherOpen && "rotate-180")} />
          </button>

          {orgSwitcherOpen && (
            <div className="mt-2 rounded-xl border border-slate-800 bg-slate-950 p-2 text-xs">
              <div className="px-2 py-1.5 text-[10px] uppercase tracking-widest text-slate-500">Switch organisation</div>
              <div className="px-2 py-2 rounded-lg bg-slate-800/40 text-slate-200">
                <div className="font-semibold">{session.orgName}</div>
                <div className="text-slate-400 text-[10px]">Active · {PORTAL_ROLE_LABEL[role] ?? role}</div>
              </div>
              <button className="mt-2 w-full text-left px-2 py-2 rounded-lg hover:bg-slate-800/40 text-slate-400">
                + Add another organisation
              </button>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto p-3 space-y-5">
          {NAV_GROUPS.map((group) => {
            const items = NAV.filter((n) => n.group === group.id);
            if (!items.length) return null;
            return (
              <div key={group.id}>
                <div className="px-3 mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">{group.label}</div>
                <div className="space-y-0.5">
                  {items.map((n) => {
                    const Icon = n.icon;
                    const active = pathname === n.href || pathname.startsWith(n.href + "/");
                    return (
                      <Link key={n.href} href={n.href}
                        className={cn(
                          "flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
                          active
                            ? "bg-blue-600/15 text-blue-200 border border-blue-700/40"
                            : "text-slate-300 hover:bg-slate-800/50 border border-transparent"
                        )}
                      >
                        <Icon className="h-4 w-4 shrink-0" />
                        <span className="flex-1">{n.label}</span>
                        {n.badge && (
                          <span className="text-[9px] font-bold tracking-widest px-1.5 py-0.5 rounded bg-red-600/20 text-red-300 border border-red-700/40 flex items-center gap-1">
                            <span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" />{n.badge}
                          </span>
                        )}
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </nav>

        {/* Footer in sidebar */}
        <div className="p-3 border-t border-slate-800 text-[10px] text-slate-500 space-y-1">
          <div>OpsCom Portal v2 · Tenant <span className="text-slate-300 font-mono">{session.tenantId}</span></div>
          <div>ICO ZB123456 · ISO 27001</div>
        </div>
      </aside>

      {/* ── Main column ─────────────────────────────────────────────────── */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Topbar */}
        <header className="h-14 border-b border-slate-800 bg-slate-900/60 backdrop-blur sticky top-0 z-20 flex items-center px-6 gap-4">
          <div className="flex-1" />
          <button className="relative h-9 w-9 rounded-lg border border-slate-800 hover:border-slate-700 grid place-items-center text-slate-300">
            <Bell className="h-4 w-4" />
            <span className="absolute -top-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-red-500 text-[9px] font-bold text-white grid place-items-center">3</span>
          </button>

          <div className="relative">
            <button
              onClick={() => setUserMenuOpen((o) => !o)}
              className="flex items-center gap-2 rounded-lg border border-slate-800 hover:border-slate-700 pl-2 pr-3 py-1.5"
            >
              <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 grid place-items-center text-xs font-bold text-white">{initials}</div>
              <div className="text-left leading-tight">
                <div className="text-xs font-semibold">{session.name}</div>
                <div className="text-[10px] text-slate-400">{PORTAL_ROLE_LABEL[role] ?? role}</div>
              </div>
              <ChevronDown className={cn("h-3.5 w-3.5 text-slate-500 transition-transform", userMenuOpen && "rotate-180")} />
            </button>
            {userMenuOpen && (
              <div className="absolute right-0 top-full mt-1 w-56 rounded-xl border border-slate-800 bg-slate-950 p-2 text-xs shadow-2xl">
                <Link href="/portal/settings" className="block px-3 py-2 rounded-lg hover:bg-slate-800/60 text-slate-200">Account settings</Link>
                <Link href="/portal/users" className="block px-3 py-2 rounded-lg hover:bg-slate-800/60 text-slate-200">Team & roles</Link>
                <div className="my-1 border-t border-slate-800" />
                <button
                  onClick={handleSignOut}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-red-600/10 text-red-300 flex items-center gap-2"
                >
                  <LogOut className="h-3.5 w-3.5" /> Sign out
                </button>
              </div>
            )}
          </div>
        </header>

        <main className="flex-1 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
